(ns app.domain.stamp.model
  (:require [cloudpermit-commons.schemas.general :as general]
            [schema-tools.core :as st]
            [schema.core :as s]))

;; Limits and units

(def max-image-bytes (* 5 1024 1024))
(def max-image-dimension 4096)
(def max-image-pixels (* 4096 4096))
(def max-output-dimension 1200)
(def image-content-types #{"image/png" "image/jpeg"})

(def points-per-inch 72)

;; Operational bounds, independent of the canvas boundary. Rendering decodes one asset at a time.
(def max-elements 100)
(def max-coordinate 14400)

;; Schemas

(s/defschema StampName
  (s/constrained general/NonBlankString #(<= (count %) 120)))

(s/defschema LocalId
  (s/constrained s/Str #(boolean (re-matches #"[A-Za-z0-9_-]{1,80}" %))))

(s/defschema Color
  (s/constrained s/Str #(boolean (re-matches #"#[0-9a-fA-F]{6}" %))))

(s/defschema Dimension
  (s/constrained s/Num
                 #(<= 0.72 % max-coordinate)
                 'physical-dimension))

(s/defschema Coordinate
  (s/constrained s/Num
                 #(<= (- max-coordinate) % max-coordinate)
                 'physical-coordinate))

(s/defschema Rotation
  (s/constrained s/Num
                 #(<= -360 % 360)
                 'rotation-degrees))

(s/defschema BorderWidth
  (s/constrained s/Num
                 #(and (< 0 %) (<= % 72))
                 'border-width-points))

(s/defschema ImageAssets
  (s/constrained {general/NonBlankString s/Int} #(<= (count %) max-elements)))

(def geometry
  {:id       LocalId
   :x        Coordinate
   :y        Coordinate
   :width    Dimension
   :height   Dimension
   :rotation Rotation})

(def text-style
  {:color     Color
   :font-size Dimension})

(s/defschema Element
  (s/conditional
    #(= :image (:type %))
    (merge geometry {:type  (s/eq :image)
                     :asset general/NonBlankString})

    #(= :text (:type %))
    (merge geometry text-style {:type (s/eq :text)
                                :text (s/constrained s/Str #(<= (count %) 4000))})

    :else
    (merge geometry text-style {:type (s/enum :date :signature)})))

(s/defschema Layout
  (s/constrained
    (st/merge {:width    Dimension
               :height   Dimension
               :elements [Element]}
              (st/optional-keys {:border       Color
                                 :border-width BorderWidth}))
    #(and (<= (count (:elements %)) max-elements)
          (apply distinct? (cons ::id (map :id (:elements %)))))
    'bounded-unique-elements))

;; Layout defaults and helpers

(def default-layout
  {:width    216
   :height   144
   :elements []})

(defn border-width [layout]
  (get layout :border-width 1))

(defn new-element [type id]
  (merge {:id       id
          :type     type
          :x        18
          :y        18
          :width    144
          :height   36
          :rotation 0}
         (when (not= type :image)
           {:color     "#111111"
            :font-size (if (= type :signature) 24 14)})
         (when (= type :text)
           {:text "Text"})))

(defn image-keys [layout]
  (into #{}
        (comp (filter #(= :image (:type %)))
              (map :asset))
        (:elements layout)))

(defn image-url [authority-group-id image-id]
  (str "/api/command/stamp/image?authority-group-id=" authority-group-id "&image-id=" image-id))

(defn image-urls [group-id assets]
  (into {} (map (fn [[key id]] [key (image-url group-id id)])) assets))

(defn has-type? [layout type]
  (boolean (some #(= type (:type %)) (:elements layout))))

(defn valid-layout? [layout]
  (nil? (s/check Layout layout)))

;; Image sizing

(defn output-dimensions
  "Fit positive source dimensions into the output cap without upscaling; round to whole pixels."
  [width height]
  (let [scale (min 1 (/ max-output-dimension (max width height)))]
    (mapv (fn [dimension]
            (-> (* scale dimension)
                double
                #?(:clj Math/round :cljs js/Math.round)
                long
                (max 1)))
          [width height])))
